import * as Sentry from '@sentry/react'
import { createContext, useCallback, useEffect, useMemo, useState } from 'react'

import { useUsersServiceHook } from '@/hooks/services/use-users-service'
import { getUser } from '@/services/get-user'
import {
  checkUserSession,
  removeUserSession,
  setUserSession,
} from '@/utils/auth'

import User from '../types/user'

interface UserData {
  token: string
  user: User
}

const useAuthContext = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState({} as User)
  const [token, setToken] = useState('')

  const isAuthenticated = useMemo(
    () => Boolean(user?.id && token),
    [user, token],
  )

  const signIn = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      if (isAuthenticated) signOut()

      const { token, user } = await getUser({ email, password })

      handleUserData({ token, user })
    },
    [isAuthenticated],
  )

  const signOut = useCallback(() => {
    removeUserSession()
    Sentry.setUser(null)
    setToken('')
    setUser({} as User)
  }, [])

  const handleUserData = useCallback(({ token, user }: UserData) => {
    setUserSession(token)
    Sentry.setUser({ email: user.email, id: user.id })
    setToken(token)
    setUser(user)
  }, [])

  const checkUserSessionWithSignOut = useCallback(
    () =>
      checkUserSession({
        onInvalidToken: () => {
          signOut()
        },
      }),
    [],
  )

  useEffect(() => {
    setIsLoading(false)
    const data = checkUserSessionWithSignOut()
    if (!data) return

    handleUserData(data)
  }, [])

  useEffect(() => {
    if (!isAuthenticated) return

    window.addEventListener('focus', checkUserSessionWithSignOut)

    return () =>
      window.removeEventListener('focus', checkUserSessionWithSignOut)
  }, [isAuthenticated])

  useUsersServiceHook({ userToken: token })

  return {
    token,
    user,
    signIn,
    signOut,
    isLoading,
    isAuthenticated,
  }
}

export type AuthContextData = ReturnType<typeof useAuthContext>

export const AuthContext = createContext<AuthContextData>(
  {} as ReturnType<typeof useAuthContext>,
)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  return (
    <AuthContext.Provider value={useAuthContext()}>
      {children}
    </AuthContext.Provider>
  )
}

// export const useAuth = () => useContext(AuthContext)
